import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Film, ArrowRight, Shield, Zap, CloudUpload, ArrowLeft } from "lucide-react";

export default function Onboarding({ onVerified, loading, setLoading }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState("email"); // "email" | "code"
  const [code, setCode] = useState("");
  const [sentEmail, setSentEmail] = useState("");
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSendCode = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@") || !trimmed.includes(".")) {
      setError("Enter a valid email address");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const axios = (await import("axios")).default;
      const res = await axios.post(`${API}/signup`, { email: trimmed });
      if (res.data.error) {
        setError(res.data.error);
      } else if (res.data.dev_bypass) {
        // Self-host bypass: backend skipped the email code, verify straight through.
        setSentEmail(trimmed);
        const v = await axios.post(`${API}/verify`, { email: trimmed, code: "000000" });
        if (v.data.error) {
          setError(v.data.error);
        } else if (v.data.verified) {
          onVerified(v.data);
        }
      } else if (res.data.sent) {
        setSentEmail(trimmed);
        setStep("code");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code from your email");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const axios = (await import("axios")).default;
      const res = await axios.post(`${API}/verify`, { email: sentEmail, code: code.trim() });
      if (res.data.error) {
        setError(res.data.error);
      } else if (res.data.verified) {
        onVerified(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setLoading(true);
    try {
      const axios = (await import("axios")).default;
      const res = await axios.post(`${API}/signup`, { email: sentEmail });
      if (res.data.error) {
        setError(res.data.error);
      } else {
        setError("");
        setCode("");
      }
    } catch (err) {
      setError("Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1114] flex items-center justify-center p-6 relative z-10">
      <div className="max-w-lg w-full">
        {/* Brand */}
        <div className="text-center mb-12">
          <div className="section-label mb-4">// by uncut gems</div>
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/gemvault-logo-64.png" alt="" className="h-9 w-9 rounded" />
            <h1 className="text-4xl md:text-5xl font-bold text-[#f4f4f5] tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              GemVault
            </h1>
          </div>
          <p className="text-[#a1a1aa] text-base max-w-sm mx-auto leading-relaxed">
            Back up your entire Vimeo library to Dropbox or Google Drive. Free, forever.
          </p>
        </div>

        {step === "email" ? (
          /* Step 1: Email */
          <div className="ug-card p-8">
            <div className="section-label mb-4">// open the vault</div>
            <p className="text-sm text-[#d4d4d8] mb-6">
              Drop your email to get started — plus you'll get first access to new tools we build for filmmakers.
            </p>
            <form onSubmit={handleSendCode} className="space-y-4">
              <Input
                data-testid="signup-email"
                type="email"
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#21252b] border-[#2a2e36] text-[#e4e4e7] placeholder:text-[#52525b] focus-visible:ring-[#a08a58] focus-visible:border-[#a08a58] h-12 text-sm rounded-xl"
              />
              {error && <p className="text-[10px] text-[#c47272]">{error}</p>}
              <button
                data-testid="signup-submit"
                type="submit"
                disabled={loading}
                className="btn-gold w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? "Sending code..." : "Send Verification Code"}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
            <p className="text-[10px] text-[#52525b] font-mono tracking-wider mt-4 text-center">
              // no spam, no selling your data. just filmmaker tools and the occasional bad pun about B-roll.
            </p>
          </div>
        ) : (
          /* Step 2: Verification code */
          <div className="ug-card p-8">
            <div className="section-label mb-4">// verify your email</div>
            <p className="text-sm text-[#d4d4d8] mb-2">
              We sent a 6-digit code to <span className="text-[#c6a96f] font-mono">{sentEmail}</span>
            </p>
            <p className="text-xs text-[#52525b] mb-6">Check your inbox (and spam folder, just in case).</p>
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <Input
                data-testid="verify-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="bg-[#21252b] border-[#2a2e36] text-[#e4e4e7] placeholder:text-[#52525b] focus-visible:ring-[#a08a58] focus-visible:border-[#a08a58] h-14 text-center text-2xl font-mono tracking-[0.5em] rounded-xl"
              />
              {error && <p className="text-[10px] text-[#c47272]">{error}</p>}
              <button
                data-testid="verify-submit"
                type="submit"
                disabled={loading || code.length !== 6}
                className="btn-gold w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? "Verifying..." : "Open the Vault"}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => { setStep("email"); setError(""); setCode(""); }} className="text-[10px] text-[#52525b] hover:text-[#c6a96f] transition-colors flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Different email
              </button>
              <button onClick={handleResend} disabled={loading} className="text-[10px] text-[#52525b] hover:text-[#c6a96f] transition-colors disabled:opacity-50">
                Resend code
              </button>
            </div>
          </div>
        )}

        {/* Vimeo plan requirement */}
        <div className="ug-card p-4 mt-8 !border-[#c6a96f]/20">
          <p className="text-xs text-[#e4e4e7] font-medium mb-1">Requires Vimeo Advanced or higher</p>
          <p className="text-[10px] text-[#71717a] leading-relaxed">
            GemVault uses the Vimeo API to download your videos. Vimeo only enables API downloads on Advanced, Pro, Business, and Enterprise plans. Starter and Standard plans do not include this feature.
          </p>
        </div>

        {/* Trust signals */}
        <div className="grid grid-cols-3 gap-4 mt-8">
          <div className="text-center">
            <Shield className="h-5 w-5 text-[#c6a96f] mx-auto mb-2" />
            <p className="text-[10px] text-[#71717a]">We never see your password</p>
          </div>
          <div className="text-center">
            <Zap className="h-5 w-5 text-[#c6a96f] mx-auto mb-2" />
            <p className="text-[10px] text-[#71717a]">2 concurrent transfers</p>
          </div>
          <div className="text-center">
            <CloudUpload className="h-5 w-5 text-[#c6a96f] mx-auto mb-2" />
            <p className="text-[10px] text-[#71717a]">Original quality files</p>
          </div>
        </div>

        {/* Legal links */}
        <div className="mt-8 text-center">
          <p className="text-[10px] text-[#3f3f46]">
            By continuing, you agree to our{" "}
            <a href="https://uncutgems.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#52525b] hover:text-[#c6a96f] transition-colors underline">Terms</a>
            {" "}and{" "}
            <a href="https://uncutgems.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#52525b] hover:text-[#c6a96f] transition-colors underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
